import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_VENUE_SLUG } from '@/lib/constants';

/**
 * Per-session venue tracking.
 *
 * <p>When a customer enters a URL under {@code /venue/:slug/*} we capture the
 * slug into this context + localStorage so navigation outside the venue
 * subtree ({@code /my-bookings}, {@code /credits}, etc.) still builds
 * correct "Book Again" and "Book Now" links that point back to the
 * customer's facility — not the hardcoded default.
 *
 * <p>{@link DEFAULT_VENUE_SLUG} is the last-resort initial value for a brand
 * new visitor whose localStorage has never been populated. It should not be
 * referenced directly by navigation components anymore — use
 * {@link useVenueContext} and read {@code currentSlug}.
 */
interface VenueContextType {
  currentSlug: string;
  setCurrentSlug: (slug: string) => void;
}

const LOCAL_STORAGE_KEY = 'rekplay_venue_slug';

const VenueContext = createContext<VenueContextType>({
  currentSlug: DEFAULT_VENUE_SLUG,
  setCurrentSlug: () => {},
});

export function VenueProvider({ children }: { children: ReactNode }) {
  const [currentSlug, setSlug] = useState<string>(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY) || DEFAULT_VENUE_SLUG;
    } catch {
      // localStorage unavailable (private browsing / disabled storage) —
      // context still works for the in-memory session.
      return DEFAULT_VENUE_SLUG;
    }
  });

  const setCurrentSlug = useCallback((slug: string) => {
    if (!slug) return;
    setSlug((prev) => {
      if (slug === prev) return prev;
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, slug);
      } catch {
        // ignore — see constructor note
      }
      return slug;
    });
  }, []);

  return (
    <VenueContext.Provider value={{ currentSlug, setCurrentSlug }}>
      {children}
    </VenueContext.Provider>
  );
}

export function useVenueContext() {
  return useContext(VenueContext);
}
